import {AsyncError, Loaded} from 'Core/models/Async';
import {eqValidatableEmail, ValidatableEmail, ValidatedEmail} from 'Core/models/Email';
import {eqStringUnion, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {
  eqValidatableNonEmptyString,
  ValidatableNonEmptyString,
  ValidatedNonEmptyString,
} from 'Core/models/ValidatableNonEmptyString';
import {
  eqValidatablePhone,
  isEmptyPhoneNumber,
  OptionalPhoneNumber,
  PhoneNumber,
  ValidatablePhone,
  ValidatedPhoneNumber,
} from 'Core/models/PhoneNumber';
import {isValid} from 'Core/models/Validatable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {getEq} from 'fp-ts/lib/Array';
import {Left} from 'fp-ts/lib/Either';
import {Eq, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {AvailableFieldSalesLeaderPayload} from 'Users/models/AvailableFieldSalesLeaderPayload';

export class UnvalidatedFieldVicePresident {
  public readonly key = 'UnvalidatedFieldVicePresident';

  constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly phoneNumber: ValidatablePhone,
    public readonly email: ValidatableEmail,
    public readonly fieldSalesLeaders: AvailableFieldSalesLeaderPayload[]
  ) {}
}

export class InvalidFieldVicePresident {
  public readonly key = 'InvalidFieldVicePresident';

  public constructor(
    public readonly firstName: StringUnion,
    public readonly lastName: StringUnion,
    public readonly username: ValidatableNonEmptyString,
    public readonly phoneNumber: ValidatablePhone,
    public readonly email: ValidatableEmail,
    public readonly fieldSalesLeaders: AvailableFieldSalesLeaderPayload[]
  ) {}
}

export class ValidFieldVicePresident {
  public static create(
    firstName: StringUnion,
    lastName: StringUnion,
    username: ValidatableNonEmptyString,
    phoneNumber: ValidatablePhone,
    email: ValidatableEmail,
    fieldSalesLeaders: AvailableFieldSalesLeaderPayload[]
  ) {
    return firstName instanceof NonEmptyString &&
      lastName instanceof NonEmptyString &&
      isValid(username) &&
      (isValid(phoneNumber) || isEmptyPhoneNumber(phoneNumber)) &&
      isValid(email)
      ? new ValidFieldVicePresident(firstName, lastName, username, phoneNumber, email, fieldSalesLeaders)
      : new InvalidFieldVicePresident(firstName, lastName, username, phoneNumber, email, fieldSalesLeaders);
  }

  public readonly key = 'ValidFieldVicePresident';
  private constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: ValidatedPhoneNumber | Loaded<Left<PhoneNumber>>,
    public readonly email: ValidatedEmail,
    public readonly fieldSalesLeaders: AvailableFieldSalesLeaderPayload[]
  ) {}
}

export class ErrorFieldVicePresident {
  public readonly key = 'ErrorFieldVicePresident';

  public constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: OptionalPhoneNumber,
    public readonly email: ValidatedEmail,
    public readonly fieldSalesLeaders: AvailableFieldSalesLeaderPayload[],
    public readonly errors: AsyncError
  ) {}
}

export class SavedFieldVicePresident {
  public readonly key = 'SavedFieldVicePresident';

  public constructor(
    public readonly firstName: NonEmptyString,
    public readonly lastName: NonEmptyString,
    public readonly username: ValidatedNonEmptyString,
    public readonly phoneNumber: ValidatedPhoneNumber | OptionalPhoneNumber,
    public readonly email: ValidatedEmail,
    public readonly fieldSalesLeaders: AvailableFieldSalesLeaderPayload[],
    public readonly id: string
  ) {}
}

export type FieldVicePresident =
  | ValidFieldVicePresident
  | InvalidFieldVicePresident
  | ErrorFieldVicePresident
  | SavedFieldVicePresident
  | UnvalidatedFieldVicePresident;

export type FieldVicePresidentOptions<TReturn> = ToMatchOptions<FieldVicePresident, 'key', TReturn>;

export const matchFieldVicePresident = <TReturn>(
  fvp: FieldVicePresident,
  options: FieldVicePresidentOptions<TReturn>
): TReturn => {
  switch (fvp.key) {
    case 'InvalidFieldVicePresident':
      return options.InvalidFieldVicePresident(fvp);
    case 'UnvalidatedFieldVicePresident':
      return options.UnvalidatedFieldVicePresident(fvp);
    case 'ValidFieldVicePresident':
      return options.ValidFieldVicePresident(fvp);
    case 'ErrorFieldVicePresident':
      return options.ErrorFieldVicePresident(fvp);
    case 'SavedFieldVicePresident':
      return options.SavedFieldVicePresident(fvp);
  }
};

export const eqFieldVicePresident: Eq<FieldVicePresident> = getStructEq<FieldVicePresident>({
  email: eqValidatableEmail,
  firstName: eqStringUnion,
  key: eqString,
  lastName: eqStringUnion,
  phoneNumber: eqValidatablePhone,
  fieldSalesLeaders: getEq(
    getStructEq({
      id: eqString,
    })
  ),
  username: eqValidatableNonEmptyString,
});

export const validateFieldVicePresident = (fvp: FieldVicePresident) =>
  ValidFieldVicePresident.create(
    fvp.firstName,
    fvp.lastName,
    fvp.username,
    fvp.phoneNumber,
    fvp.email,
    fvp.fieldSalesLeaders
  );
